<template>
  <v-container class="itinerary">
    <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
    <h1>IABF {{ festivalYear }} Itinerary</h1>

    <div v-if="!effectivelyPublished" class="placeholder-card">
      <div class="placeholder-icon">✨</div>
      <h2 class="placeholder-title">Coming soon</h2>
    </div>

    <template v-else>
    <div v-if="published === false && isAdmin" class="admin-preview-banner">
      🔒 Admin preview · this view is hidden from dancers until you publish.
    </div>
    <div v-if="isAdmin" class="admin-test-bar">
      <span class="test-label">🧪 Test time:</span>
      <select v-model="testDay" class="test-input">
        <option value="">(real day)</option>
        <option value="tuesday">Tuesday</option>
        <option value="wednesday">Wednesday</option>
        <option value="thursday">Thursday</option>
        <option value="friday">Friday</option>
      </select>
      <input v-model="testTime" type="time" class="test-input" />
      <button type="button" class="test-btn" @click="applyTest('tuesday', '13:00')">Tue 1:00 PM</button>
      <button type="button" class="test-btn" @click="applyTest('friday', '18:00')">Fri 6:00 PM</button>
      <button v-if="testDay || testTime" type="button" class="test-btn test-btn-clear" @click="clearTest">Clear</button>
      <span v-if="testDay || testTime" class="test-active">← overriding now</span>
    </div>
    <v-tabs v-model="activeDay" class="day-tabs" background-color="transparent">
      <v-tab v-for="d in daysWithClasses" :key="d.key" :href="`#${d.key}`">{{ d.label }}</v-tab>
    </v-tabs>

    <div v-if="teachers.length > 1" class="teacher-filter">
      <span class="filter-label">Filter by teacher:</span>
      <button type="button" class="teacher-chip" :class="{ active: !selectedTeacher }" @click="selectedTeacher = ''">All</button>
      <button v-for="t in teachers" :key="t" type="button" class="teacher-chip" :class="{ active: selectedTeacher === t }" @click="selectedTeacher = t">{{ t }}</button>
    </div>

    <div v-if="loading" class="muted pad-y">Loading…</div>

    <div v-else>
      <div v-for="d in daysWithClasses" :key="d.key" v-show="activeDay === d.key">
        <details v-if="pastGroupsForDay(d.key).length > 0" class="past-collapse">
          <summary class="past-summary">Earlier today ({{ pastGroupsForDay(d.key).length }} slot{{ pastGroupsForDay(d.key).length === 1 ? '' : 's' }} · ended over 1 hr ago) — tap to show</summary>
          <div v-for="slot in pastGroupsForDay(d.key)" :key="slot.time" class="slot-section slot-past">
            <div class="slot-time">{{ slot.time }}</div>
            <div
              v-for="cls in slot.classes"
              :key="cls.id"
              class="class-card"
              :class="{ 'card-meal': cls.track === 'all' }"
            >
              <div v-if="cls.track !== 'all'" class="card-tracks">
                <span v-for="t in displayTracksFor(cls)" :key="t" class="track-chip">{{ TRACK_LABEL[t] || t }}</span>
              </div>
              <div class="card-title">{{ cls.title || '(untitled)' }}</div>
              <div v-if="cls.teacher" class="card-teacher">{{ cls.teacher }}</div>
              <div v-if="cls.hasAccompanist" class="card-accompanist">🎹 Live piano</div>
              <div v-if="cls.notes" class="card-notes">{{ cls.notes }}</div>
              <div class="card-room" :class="{ 'card-room-tba': !(cls.room || cls.defaultRoom) }">
                <span class="pin">📍</span>
                <span>{{ cls.room || cls.defaultRoom || 'Room TBA' }}</span>
              </div>
              <div v-if="cls.description" class="card-description">{{ cls.description }}</div>
            </div>
          </div>
        </details>
        <div v-for="slot in activeGroupsForDay(d.key)" :key="slot.time" class="slot-section">
          <div class="slot-time">{{ slot.time }}</div>
          <div
            v-for="cls in slot.classes"
            :key="cls.id"
            class="class-card"
            :class="{ 'card-meal': cls.track === 'all' }"
          >
            <div v-if="cls.track !== 'all'" class="card-tracks">
              <span v-for="t in displayTracksFor(cls)" :key="t" class="track-chip">{{ TRACK_LABEL[t] || t }}</span>
            </div>
            <div class="card-title">{{ cls.title || '(untitled)' }}</div>
            <div v-if="cls.teacher" class="card-teacher">{{ cls.teacher }}</div>
            <div v-if="cls.hasAccompanist" class="card-accompanist">🎹 Live piano</div>
            <div v-if="cls.notes" class="card-notes">{{ cls.notes }}</div>
            <div class="card-room" :class="{ 'card-room-tba': !(cls.room || cls.defaultRoom) }">
              <span class="pin">📍</span>
              <span>{{ cls.room || cls.defaultRoom || 'Room TBA' }}</span>
            </div>
            <div v-if="cls.description" class="card-description">{{ cls.description }}</div>
          </div>
        </div>
      </div>
    </div>
    </template>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import { IABF } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'


const TRACK_LABEL = {
  rehearsal:  'Rehearsal',
  begBallet:  'Beg. Ballet',
  intBallet:  'Int Ballet',
  contemp:    'Contemporary'
}

const DAY_LABEL = {
  tuesday:   'Tuesday',
  wednesday: 'Wednesday',
  thursday:  'Thursday',
  friday:    'Friday'
}

// See ItineraryAdmin.vue — parses "9-9:45"-style labels into start/end
// minute counts. Used only for ordering within a day.
//
// Labels are 12-hour ("1-2" = 1 PM to 2 PM). Any hour 1-6 gets bumped +12
// so afternoon slots sort after morning ones. Nothing at IABF starts before
// ~7 AM, so hours 7-12 stay as-is.
function parseSlotRange (label) {
  const m = String(label || '').match(/(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/)
  if (!m) return { start: 0, end: 0 }
  const toPm = (h) => (h >= 1 && h <= 6) ? h + 12 : h
  const sh = toPm(parseInt(m[1], 10))
  const sm = m[2] ? parseInt(m[2], 10) : 0
  const eh = toPm(parseInt(m[3], 10))
  const em = m[4] ? parseInt(m[4], 10) : 0
  return { start: sh * 60 + sm, end: eh * 60 + em }
}

function parse24h (s) {
  const m = String(s || '').match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

function classSortKey (c) {
  const startCanon = parse24h(c.startTime || '')
  const endCanon = parse24h(c.endTime || '')
  if (startCanon !== null) {
    return { start: startCanon, end: endCanon !== null ? endCanon : startCanon }
  }
  const fromLabel = parseSlotRange(c.timeLabel || '')
  return { start: fromLabel.start || (c.order || 0), end: fromLabel.end || 0 }
}

export default {
  data () {
    return {
      loading: true,
      classes: [],
      activeDay: 'tuesday',
      selectedTeacher: '',
      TRACK_LABEL,
      // Published state is loaded from iabf-config-2026/itinerary so the
      // admin can flip it without a redeploy. null while loading; admin
      // sees the full view regardless via the effectivelyPublished gate.
      published: null,
      // Minutes-from-midnight for "now" — refreshed every 60s so the past-
      // classes collapse rolls forward through the day without a reload.
      nowMinutes: (() => {
        const d = new Date()
        return d.getHours() * 60 + d.getMinutes()
      })(),
      _nowTimer: null,
      // Admin-only test override so Julie can preview the past-hour collapse
      // outside the actual festival window. Empty string = use real time.
      testDay: '',
      testTime: ''
    }
  },
  computed: {
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) || IABF_DANCER_YEAR
    },
    isAdmin () {
      const user = this.$store.getters.user
      return !!(user && user.email && user.email.toLowerCase() === 'julie@brocheballet.com')
    },
    effectivelyPublished () {
      // Public visibility is gated by the iabf-config-2026/itinerary
      // doc's published field. Admin always sees the full view so it
      // can be previewed before flipping the switch.
      return this.published === true || this.isAdmin
    },
    daysWithClasses () {
      const seen = {}
      for (const c of this.classes) {
        if (!DAY_LABEL[c.day]) continue
        if (!seen[c.day]) seen[c.day] = c.dayOrder
      }
      return Object.keys(seen)
        .sort((a, b) => seen[a] - seen[b])
        .map(d => ({ key: d, label: DAY_LABEL[d] }))
    },
    todayKey () {
      // Which festival-day tab, if any, matches today's day of week.
      // 2=Tue, 3=Wed, 4=Thu, 5=Fri per Date#getDay().
      if (this.testDay) return this.testDay
      const map = { 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday' }
      return map[new Date().getDay()] || ''
    },
    effectiveNowMinutes () {
      if (this.testTime) {
        const m = this.testTime.match(/^(\d{1,2}):(\d{2})$/)
        if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
      }
      return this.nowMinutes
    },
    teachers () {
      // Distinct teacher names on the active day, sorted by last name.
      // A few entries are intentionally skipped from the filter chips —
      // choreographers we don't want dancers filtering on, and combo
      // entries like "Sean, Mikey, Krystal" that are too long to be a
      // useful chip.
      const skip = new Set([
        'Kelsey Maloney-Steiner',
        'Sean, Mikey, Krystal'
      ])
      const set = new Set()
      for (const c of this.classes) {
        if (c.day !== this.activeDay) continue
        const t = (c.teacher || '').trim()
        if (!t) continue
        if (skip.has(t)) continue
        // Also skip anything that looks like a multi-teacher combo
        // (has a comma) — those classes still get matched if a single
        // teacher in the combo is selected, since exact-equality won't
        // match anyway.
        if (t.includes(',')) continue
        set.add(t)
      }
      return Array.from(set).sort((a, b) => {
        const al = a.split(/\s+/).pop()
        const bl = b.split(/\s+/).pop()
        return al.localeCompare(bl)
      })
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    try {
      const cfg = await db.collection(IABF.config).doc('itinerary').get()
      this.published = !!(cfg.exists && cfg.data().published)
    } catch (e) {
      this.published = false
    }
    if (!this.effectivelyPublished) {
      this.loading = false
      return
    }
    const snap = await db.collection(IABF.classes).get()
    this.classes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.loading = false
    this._nowTimer = setInterval(() => {
      const d = new Date()
      this.nowMinutes = d.getHours() * 60 + d.getMinutes()
    }, 60000)
  },
  beforeDestroy () {
    if (this._nowTimer) clearInterval(this._nowTimer)
  },
  methods: {
    classMatchesSelectedTeacher (cls) {
      if (!this.selectedTeacher) return true
      if (!cls) return false
      const sel = this.selectedTeacher.toLowerCase()
      const teacher = (cls.teacher || '').toLowerCase()
      if (!teacher) return false
      if (teacher === sel) return true
      return teacher.split(',').map(s => s.trim()).includes(sel)
    },
    slotGroupsForDay (day) {
      // Group every distinct class doc on this day under its timeLabel.
      // No track-column duplication — all-row meals appear once. When a
      // teacher is selected, drop non-matching classes; drop any slot that
      // ends up empty so the screen scrolls cleanly between matches.
      const slots = new Map()
      for (const c of this.classes) {
        if (c.day !== day) continue
        if (this.selectedTeacher && !this.classMatchesSelectedTeacher(c)) continue
        const key = c.timeLabel || ''
        if (!slots.has(key)) slots.set(key, [])
        slots.get(key).push(c)
      }
      const groups = []
      for (const [time, classes] of slots) {
        // Within a slot, put the meal/all-row first so it's the visual
        // header for the time block; track classes follow in track order.
        classes.sort((a, b) => {
          if ((a.track === 'all') !== (b.track === 'all')) return a.track === 'all' ? -1 : 1
          return 0
        })
        // Prefer the class's canonical 24-hour startTime/endTime when set —
        // that's the only reliable way to disambiguate AM vs PM (e.g. a
        // 7:45 PM class vs. 8 AM breakfast). Fall back to the label parser.
        const first = classes[0]
        const canonStart = first ? parse24h(first.startTime || '') : null
        const canonEnd = first ? parse24h(first.endTime || '') : null
        const range = parseSlotRange(time)
        groups.push({
          time,
          classes,
          sort: canonStart !== null ? canonStart : (range.start || (first && first.order) || 0),
          end: canonEnd !== null ? canonEnd : range.end
        })
      }
      groups.sort((a, b) => {
        if (a.sort !== b.sort) return a.sort - b.sort
        return b.end - a.end
      })
      return groups
    },
    isPastForToday (day, slotEnd) {
      // Only apply the "past classes" collapse when the viewed day is
      // today. A slot counts as past when it ended more than 60 minutes
      // ago (i.e. its end time is before now-60min).
      if (day !== this.todayKey) return false
      if (!slotEnd) return false
      return slotEnd < this.effectiveNowMinutes - 60
    },
    applyTest (day, time) {
      this.testDay = day
      this.testTime = time
    },
    clearTest () {
      this.testDay = ''
      this.testTime = ''
    },
    pastGroupsForDay (day) {
      return this.slotGroupsForDay(day).filter(g => this.isPastForToday(day, g.end))
    },
    activeGroupsForDay (day) {
      return this.slotGroupsForDay(day).filter(g => !this.isPastForToday(day, g.end))
    },
    displayTracksFor (cls) {
      // Which track chips to show on this class's card. Prefer the tracks[]
      // array (covers multi-track classes), fall back to the single `track`
      // field for legacy entries.
      const ts = Array.isArray(cls.tracks) && cls.tracks.length ? cls.tracks : (cls.track ? [cls.track] : [])
      return ts.filter(t => t !== 'all')
    }
  },
  watch: {
    // Clear the teacher filter when switching days, since the teacher
    // pool may differ.
    activeDay () { this.selectedTeacher = '' }
  }
}
</script>

<style scoped>
.itinerary { max-width: 1100px; padding-bottom: 60px; }
.past-collapse { margin: 8px 0 16px; }
.past-summary { cursor: pointer; padding: 10px 14px; background: #f2ede1; border: 1px dashed #d4c9a8; border-radius: 6px; font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #7a6828; list-style: none; }
.past-summary::-webkit-details-marker { display: none; }
.past-summary::before { content: '▸ '; color: #a09069; }
.past-collapse[open] .past-summary::before { content: '▾ '; }
.past-summary:hover { background: #ebe4d0; }
.slot-past { opacity: .6; }
.admin-test-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 8px 0 12px; padding: 8px 12px; background: #fff8dc; border: 1px dashed #d4b800; border-radius: 6px; font-family: var(--iabf-font-body, inherit); font-size: 13px; }
.test-label { color: #6a5200; font-weight: 700; }
.test-input { padding: 3px 6px; border: 1px solid #d4b800; border-radius: 4px; font-size: 13px; font-family: inherit; background: #fff; }
.test-btn { padding: 4px 10px; border: 1px solid #d4b800; border-radius: 4px; background: #fff; cursor: pointer; font-family: inherit; font-size: 12px; color: #6a5200; }
.test-btn:hover { background: #f5e58a; }
.test-btn-clear { color: #a94442; border-color: #a94442; }
.test-active { color: #a94442; font-style: italic; }
.back-link { font-family: var(--iabf-font-body, inherit); font-size: 14px; color: var(--iabf-ink-faint, #8a8792); text-decoration: none; }
.back-link:hover { color: var(--iabf-ink, #1c1b1f); }

.placeholder-card { background: var(--iabf-bg, #f7f6f4); border: 1px solid var(--iabf-border, #e3e0e8); border-radius: 8px; padding: 36px 28px; margin: 24px 0; text-align: center; max-width: 480px; }
.placeholder-icon { font-size: 32px; margin-bottom: 8px; }
.placeholder-title { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 400; font-size: 22px; color: var(--iabf-primary, #8a957d); margin: 0 0 10px; }
.placeholder-body { font-family: var(--iabf-font-body, inherit); font-size: 15px; color: #555; margin: 0; line-height: 1.5; }
.admin-preview-banner { background: #fff8e1; border: 1px solid #f4c63d; border-radius: 4px; padding: 8px 14px; margin: 12px 0; font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #8a6d00; }
h1 { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 400; font-size: 32px; margin: 12px 0 4px 0; }
.subtitle { font-family: var(--iabf-font-body, inherit); font-size: 15px; color: #777; margin: 0 0 16px 0; }
.muted { color: var(--iabf-ink-faint, #8a8792); font-style: italic; font-size: 14px; }
.pad-y { padding: 30px 0; }
.day-tabs { margin-bottom: 12px; }

.teacher-filter { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 4px 0 16px; padding: 10px 14px; background: #faf8f3; border-radius: 8px; }
.filter-label { font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #555; font-weight: 600; margin-right: 4px; }
.teacher-chip { background: #fff; border: 1px solid #ddd; border-radius: 999px; padding: 5px 12px; font-family: var(--iabf-font-body, inherit); font-size: 13px; color: var(--iabf-ink, #1c1b1f); cursor: pointer; transition: all 0.12s; }
.teacher-chip:hover { background: #f5f3ed; }
.teacher-chip.active { background: var(--iabf-primary, #8a957d); color: #fff; border-color: var(--iabf-primary, #8a957d); font-weight: 700; }

.slot-section { margin: 16px 0 22px; }
.slot-time { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 700; font-size: 17px; color: var(--iabf-danger, #b54a4a); margin: 0 0 8px 2px; }

.class-card { background: #fff; border: 1px solid #ece9e2; border-radius: 10px; padding: 12px 14px; margin-bottom: 8px; }
.class-card:last-child { margin-bottom: 0; }
.class-card.card-meal { background: #eef6f8; border-color: #c8dde2; }

.card-tracks { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.track-chip { display: inline-block; font-family: var(--iabf-font-body, inherit); font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: #777; background: #faf8f3; padding: 2px 8px; border-radius: 999px; }
.card-title { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 700; font-size: 18px; color: var(--iabf-ink, #1c1b1f); line-height: 1.25; margin-bottom: 4px; }
.card-teacher { font-family: var(--iabf-font-body, inherit); font-size: 14px; color: var(--iabf-ink-muted, #5c5a63); margin-bottom: 4px; }
.card-accompanist { font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #1a4452; font-weight: 700; margin-bottom: 4px; }
.card-notes { font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #777; font-style: italic; margin-bottom: 4px; }
.card-room { display: flex; align-items: center; gap: 6px; font-family: var(--iabf-font-body, inherit); font-size: 14px; color: var(--iabf-primary, #8a957d); font-weight: 700; margin: 6px 0; }
.card-room.card-room-tba { color: var(--iabf-danger, #b54a4a); font-style: italic; font-weight: 400; }
.pin { font-size: 14px; }
.card-description { font-family: var(--iabf-font-body, inherit); font-size: 14px; color: var(--iabf-ink-muted, #5c5a63); line-height: 1.5; padding: 10px 12px; margin-top: 8px; background: #faf8f3; border-left: 3px solid var(--iabf-warning, #c4923a); border-radius: 0 6px 6px 0; }

@media (min-width: 768px) {
  .itinerary { max-width: 720px; }
  .card-title { font-size: 19px; }
}
</style>
